import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { doCreateJWTToken } from '../api/usersService';
import AppLoading from '../components/App/AppLoading';
import { useQuery } from '../hooks';
import { useAuth } from '../services/auth0';
import { logger } from '../services/logger';
import { tempRedirectInfoState } from '../store/global.store';
import { useNavigateToErrorPage } from './ErrorPages/ErrorPage';

const allowedRedirectDomains = [
  '.usepylon.com',
  '.monetizeplatform.com',
  '.monetizenow.io',
  '.monetizenow.ai',
];

/**
 * Handles login with Pylon SSO via JWT
 * * If user is authenticated, then we issue a JWT and redirect to Pylon
 * * If user is not authenticated, then we redirect to the login page and redirected back here with exact same URL
 * * Once the user is authenticated, we generate a JWT and redirect back to the url pylon provided to us
 */
export const PylonJwtRedirector = () => {
  const {
    currentTenant,
    isAuthenticated,
    loginWithRedirect,
    userId: currentUserId,
  } = useAuth();
  const setRedirectInfo = useSetAtom(tempRedirectInfoState);
  const navigateToErrorPage = useNavigateToErrorPage();
  const query = useQuery();
  const redirectUrl = query.get('redirect_uri');

  const handleRedirect = async () => {
    try {
      if (!redirectUrl) {
        navigateToErrorPage('Missing redirect URL.');
        return;
      }

      const url = new URL(redirectUrl);
      // Ensure the redirect is to an expected domain
      if (
        !allowedRedirectDomains.some((domain) => url.hostname.endsWith(domain))
      ) {
        navigateToErrorPage('Invalid redirect URL.');
        return;
      }

      if (!isAuthenticated) {
        // come back here after authentication
        setRedirectInfo({ redirectUrl: location.href });
        await loginWithRedirect({
          authorizationParams: { screen_hint: 'login' },
        });
        return;
      }

      // should never happen since user should be provisioned
      if (!currentUserId) {
        navigateToErrorPage(
          'There was an error retrieving your user information, ensure your user is fully provisioned and try again.',
        );
        return;
      }

      const { token } = await doCreateJWTToken('https://portal.usepylon.com');
      url.searchParams.set('access_token', token);
      location.href = url.toString();
    } catch (error) {
      logger.error('Error during Pylon SSO redirect', { error });
      navigateToErrorPage('There was an error processing your request.');
    }
  };

  useEffect(() => {
    handleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant, currentUserId, redirectUrl]);

  return <AppLoading />;
};
